import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FolderOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api, errMsg } from '../api';
import { PageHeader, Card, SectionTitle, goldBtn, inputCls } from '../ui';
import SourcePicker from '../dossier/SourcePicker';
import DossierList, { downloadDossier } from '../dossier/DossierList';
import QuickDossier from '../dossier/QuickDossier';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const DESCRIBE = {
  reports: (r) => ({ label: r.company_name, sub: r.company_id }),
  aml: (c) => ({ label: c.label, sub: plural(c.subjects, 'soggetto', 'soggetti'), disabled: c.pending, warn: c.pending && 'Soggetto da confermare o verifica in corso.', link: `/portal/reputational/${c.id}` }),
  property: (p) => ({ label: p.name, sub: `${p.code} · ${plural(p.files, 'visura', 'visure')}` }),
  negative: (n) => ({ label: n.name, sub: `${n.code} · ${n.positive ? (n.opened ? 'esito positivo con dettaglio' : 'esito positivo, dettaglio non aperto') : 'nessuna negatività'}` }),
};

function Option({ id, checked, disabled, onChange, children }) {
  return (
    <label className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`} data-testid={`dossier-opt-${id}`}>
      <input type="checkbox" checked={checked && !disabled} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="accent-[#D4AF37]" data-testid={`dossier-opt-${id}-input`} />{children}
    </label>
  );
}

export default function DossierPage() {
  const [src, setSrc] = useState(null);
  const [list, setList] = useState(null);
  const [title, setTitle] = useState('');
  const [sel, setSel] = useState({ reports: [], aml: [], property: [], negative: [] });
  const [graphs, setGraphs] = useState({ company: true, aml: true });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const fromReport = params.get('report');

  useEffect(() => {
    api.get('/dossier/sources', { params: fromReport ? { report_id: fromReport } : {} }).then((r) => {
      setSrc(r.data);
      const s = r.data.suggested;
      if (s) { setTitle(s.title); setSel({ reports: s.report_ids, aml: s.aml_ids, property: s.property_ids, negative: s.negative_ids || [] }); }
    }).catch((e) => toast.error(errMsg(e)));
    api.get('/dossier').then((r) => setList(r.data)).catch(() => setList([]));
  }, [fromReport]);

  const hasPending = (list || []).some((d) => d.pending);
  useEffect(() => {
    if (!hasPending) return undefined;
    const t = setInterval(() => api.get('/dossier').then((r) => setList(r.data)).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [hasPending]);

  const toggle = (k) => (id) => setSel((s) => ({ ...s, [k]: s[k].includes(id) ? s[k].filter((x) => x !== id) : [...s[k], id] }));
  const total = sel.reports.length + sel.aml.length + sel.property.length + sel.negative.length;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/dossier', { title, report_ids: sel.reports, aml_ids: sel.aml, property_ids: sel.property, negative_ids: sel.negative,
        company_graph: graphs.company, aml_graph: graphs.aml, cover: localStorage.getItem('bond_pdf_cover') || 'a' });
      setList((l) => [data, ...(l || [])]);
      setSel({ reports: [], aml: [], property: [], negative: [] }); setTitle('');
      await downloadDossier(data);
      toast.success('Dossier creato');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader kicker="Report aggregato" title="Dossier" />
      <Card className="p-6 mb-6 flex flex-wrap items-center gap-4" data-testid="quick-dossier-card">
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm text-[#E5E5E5]">Dossier rapido</p>
          <p className="text-xs text-[#8A8F97] mt-1">Report azienda + reputational check sul legale rappresentante, in un unico PDF creato appena la verifica è completa.</p>
        </div>
        <QuickDossier />
      </Card>
      <Card className="p-6 mb-8">
        <SectionTitle right={<span className="text-xs font-mono text-[#5FBF8E]" data-testid="dossier-free">Gratuito</span>}>Nuovo dossier</SectionTitle>
        <p className="text-sm text-[#8A8F97] mb-5">Scegli report aziendali, reputational check, visure immobiliari e negatività già acquistati: li uniamo in un unico PDF con indice, grafi e visure complete in appendice.</p>
        {src?.suggested && <p className="text-sm text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-3 mb-5" data-testid="dossier-prefilled">Abbiamo già selezionato il report e le verifiche collegate a {src.suggested.title.replace('Dossier ', '')}: controlla e conferma.</p>}
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo del dossier, es. Due diligence Rossi S.p.A." className={`${inputCls} mb-5`} data-testid="dossier-title-input" />
        {!src ? <Loader2 size={18} className="animate-spin text-[#D4AF37]" /> : (
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
            <SourcePicker id="reports" title="Report aziendali" items={src.reports} selected={sel.reports} onToggle={toggle('reports')} empty="Nessun report acquistato." describe={DESCRIBE.reports} />
            <SourcePicker id="aml" title="Reputational check (AML)" items={src.aml} selected={sel.aml} onToggle={toggle('aml')} empty="Nessun reputational check eseguito." describe={DESCRIBE.aml} />
            <SourcePicker id="property" title="Immobili (visure catastali)" items={src.property} selected={sel.property} onToggle={toggle('property')} empty="Nessuna visura pronta." describe={DESCRIBE.property} />
            <SourcePicker id="negative" title="Negatività persone" items={src.negative || []} selected={sel.negative} onToggle={toggle('negative')} empty="Nessuna ricerca negatività completata." describe={DESCRIBE.negative} />
          </div>
        )}
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
          <Option id="company-graph" checked={graphs.company} disabled={!sel.reports.length} onChange={(v) => setGraphs((g) => ({ ...g, company: v }))}>Grafo relazioni aziendali</Option>
          <Option id="aml-graph" checked={graphs.aml} disabled={!sel.aml.length} onChange={(v) => setGraphs((g) => ({ ...g, aml: v }))}>Grafo AML</Option>
        </div>
        <Button onClick={create} disabled={!total || title.trim().length < 2 || busy} className={`${goldBtn} h-11 mt-6 px-6`} data-testid="dossier-create-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><FolderOpen size={15} className="mr-2" />Crea dossier e scarica PDF {total ? `(${total})` : ''}</>}
        </Button>
      </Card>
      <DossierList items={list} setItems={setList} />
    </div>
  );
}
